import styled from "styled-components";
import DeleteIcon from "../../assets/icons/svgs/delete.svg";
import { PrimaryBtn } from "../../components/BasicComponents";
import "./styles/CreateGiftCard.css";
import { useState, useEffect } from "react";
import { FaPlus } from "react-icons/fa";
import CustomDropdown from "../../components/CustomDropdown";

import instance from "../../axios";
import { useParams } from "react-router";
import fieldValidate from "../../utils/fieldValidate";
import { createPortal } from "react-dom";
import { getUserToken } from "../../utils/userAuthenticate";
import Spinner from "../../components/Loaders/Spinner";
import {gcValidityMonths, gcValidityYears} from "../../assets/currency"
import { useTranslation } from "react-i18next";
import "../../localisation/index"

import GiftcardGallery from "../../components/GiftcardGallery";
import {sanitizeInput} from "@/hooks/useInputSanitization";
const ActiveDot = styled.div`
  width: 15px;
  height: 15px;
  margin: 0px 10px;
  border-radius: 20px;
  background: ${({ active }) => (active ? "#00dc72" : "")};
  border: 1px solid ${({ active }) => (active ? "#00dc72" : "#B62028")};
`;

const EditGiftCard = () => {
  const {t} = useTranslation()
  // id by params
  const { id } = useParams();

  // data values
  const [cardData, setCardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(null);
  const [activeValidity, setActiveValidity] = useState("");
  const [selectedImage, setSelectedImage] = useState('')

  // handle on change
  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    if(name==="terms" || name==="description") {
      setCardData((prev) => ({ ...prev, [name]: value }));
    } else if (name === "show_barcode") {
      setCardData((prev) => ({ ...prev, [name]: !prev.show_barcode }))
    } else if (name === "allow_denomination") {
      setCardData((prev) => ({ ...prev, [name]: !prev.allow_denomination }))
    } else if (name === "max_denomination" || name === "min_denomination") {
      setCardData((prev) => ({ ...prev, [name]: parseInt(value) }))
    } else if (name === "self_purchase_quantity" || name === "gift_quantity") {
      // Allow only integers between 1 and 10
      if (/^\d{0,2}$/.test(value)) {
          const parsedValue = parseInt(value);
          if (parsedValue >= 1 && parsedValue <= 10) {
              setCardData((prev) => ({ ...prev, [name]: parsedValue || "" }));
              setIsError(null);
          } else if (value !== "") {
              setIsError(t("createGiftcard.quantityValidationError"));
          } else {
              setCardData((prev) => ({ ...prev, [name]: "" })); 
              setIsError(null); 
          }
      } else {
          setIsError(t("createGiftcard.quantityValidationError"));
      }
  } else if (/^[a-zA-Z0-9.\s]*$/.test(value)) {
      setCardData((prev) => ({ ...prev, [name]: value }));
  }
  };

  const handleVariant = (event) => {
    const index = event.target.id;
    const name = event.target.name;
    const value = event.target.value;

    // console.log(index+name+value)
    if (name === "price") {
      if (/^(?!.*\..*\.)[0-9]*(\.[0-9]*)?$/.test(value)) {
        setCardData((prev) => {
          const updatedVariants = [...prev.variants];
          updatedVariants[index][name] = value;
          return { ...prev, variants: updatedVariants };
        });
      }
    } else if (/^[a-zA-z0-9.\s]*$/.test(value)) {
      setCardData((prev) => {
        const updatedVariants = [...prev.variants];
        updatedVariants[index][name] = value;
        return { ...prev, variants: updatedVariants };
      });
    }
  };

  // variant append and delete
  const handleAppend = () => {
    setCardData((prev) => ({
      ...prev,
      variants: [...prev.variants, { option1: "", price: "" }],
    }));
  };

  const handleDelete = (event) => {
    const index = event.target.id;

    setCardData((prev) => {
      const dt = { ...prev };
      const arr = dt.variants;
      arr.splice(index, 1);
      return dt;
    });
    console.log(index);
  };

  //fetch
  const fetchData = async () => {
    setIsLoading(true);
    const url = "/giftcard/products/select";
    const headers = {
      Authorization: getUserToken(),
    };
    const body = {
      product_id: id,
    };

    try {
      const res = await instance.post(url, body, { headers });
      const resData = res.data;
      let validityType
      if (!('expiration' in resData.data)) {
        validityType = "months"
      } else if ('expiration' in resData.data && resData.data.expiration === false) {
        validityType = "no_expiry"
      } else if ('expiry_month' in resData.data) {
        validityType = 'months'
      } else if ('expiry_year' in resData.data) {
        validityType = 'years'
      }
      validityType && setActiveValidity(validityType);
      ('show_barcode' in resData.data) ? setCardData(resData.data) : setCardData({ ...resData.data, show_barcode: false });
      setSelectedImage(resData.data.templateImages[0].src || resData.data.images[0].src || '')
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  //   update data
  const handleUpdate = async () => {
    setIsError(null);
    setIsLoading(true);

    if (activeValidity === "months" && "expiry_year" in cardData) {
        delete cardData.expiry_year;
    } else if (activeValidity === "years" && "expiry_month" in cardData) {
        delete cardData.expiry_month;
    }

    const url = "/giftcard/products/update";
    const headers = {
        Authorization: getUserToken(),
    };

    const imageAttachment = selectedImage
        ? selectedImage.split(",")[1] 
        : cardData.templateImages?.[0]?.attachment || cardData.images?.[0]?.src || "";

    const body = {
        product_id: id,
        title: cardData.title.trim(),
        description: cardData.body_html.trim(),
        variants: cardData.variants,
        ...("expiration" in cardData && { expiration: cardData.expiration }),
        ...(activeValidity === "months" && { expiry_month: cardData.expiry_month }),
        ...(activeValidity === "years" && { expiry_year: cardData.expiry_year }),
        terms: cardData?.terms.trim(),
        show_barcode: cardData?.show_barcode,
        allow_denomination: cardData?.allow_denomination,
        ...(cardData?.allow_denomination && { min_denomination: Number(cardData?.min_denomination) }),
        ...(cardData?.allow_denomination && { max_denomination: Number(cardData?.max_denomination) }),
        images: imageAttachment ? [{ attachment: imageAttachment }] : cardData.images, 
        self_purchase_quantity: Number(cardData.self_purchase_quantity),
        gift_quantity: Number(cardData.gift_quantity),
    };

    // Field validation
    if (!fieldValidate(cardData.title, 3)) {
        setIsError(t("createGiftcard.titleError"));
        setIsLoading(false);
        return;
    } else if (!fieldValidate(cardData.body_html, 3)) {
        setIsError(t("createGiftcard.descriptionError"));
        setIsLoading(false);
        return;
    } else if (!fieldValidate(cardData.terms, 3)) {
        setIsError(t("createGiftcard.termsError"));
        setIsLoading(false);
        return;
    } else if (
        cardData.variants.length === 0 ||
        cardData.variants.some((item) => item.option1 === "" || item.price === "")
    ) {
        setIsError(t("createGiftcard.variantError"));
        setIsLoading(false);
        return;
    } else if (!imageAttachment && (!cardData.images || cardData.images.length === 0)) {
        setIsError(t("createGiftcard.imageError"));
        setIsLoading(false);
        return;
    } else if (
        (activeValidity === "months" && !cardData.expiry_month) ||
        (activeValidity === "years" && !cardData.expiry_year)
    ) {
        setIsError(t("createGiftcard.validityError"));
        setIsLoading(false);
        return;
    } else if (
        !cardData.self_purchase_quantity ||
        !cardData.gift_quantity
    ) {
        setIsError(t("createGiftcard.quantityError"));
        setIsLoading(false);
        return;
    } else {
        setIsError(null);
    }

    try {
        const res = await instance.put(url, body, { headers });
        const resData = res.data;
        alert(resData.message);
    } catch (error) {
        console.log(error);
    } finally {
        setIsLoading(false);
    }
};

  const renderValidityUi = () => {
    // Helper function to render the radio buttons
    const renderRadioButtons = () => (
      <div className="gift-card__validity-radio">
        <label>
          <input
            type="radio"
            name="validity"
            checked={activeValidity === "months"}
            onChange={() => {
              setCardData((prev) => ({ ...prev, expiration: true }));
              setActiveValidity("months");
            }}
          />
          {t("createGiftcard.months")}
        </label>
        <label>
          <input
            type="radio"
            name="validity"
            checked={activeValidity === "years"}
            onChange={() => {
              setCardData((prev) => ({ ...prev, expiration: true }));
              setActiveValidity("years");
            }}
          />
          {t("createGiftcard.years")}
        </label>
        <label>
          <input
            type="radio"
            name="validity"
            checked={activeValidity === "no_expiry"}
            onChange={() => {
              setCardData((prev) => ({ ...prev, expiration: false }));
              setActiveValidity("no_expiry");
            }}
          />
          {t("createGiftcard.noExpiry")}
        </label>
      </div>
    );

    return (
      <>
        {renderRadioButtons()}
        <div className="gift-card__validity">
          {activeValidity === "months" && (
            <CustomDropdown
              options={gcValidityMonths}
              keyField="expiry_month"
              value={cardData?.expiry_month}
              setvalue={setCardData}
            />
          )}
          {activeValidity === "years" && (
            <CustomDropdown
              options={gcValidityYears}
              keyField="expiry_year"
              value={cardData?.expiry_year}
              setvalue={setCardData}
            />
          )}
        </div>
      </>
    );
  };

  return (
    cardData !== null && (
      <div className="gift-card__container section-box-container component">
        <div className="gift-card__active-status">
          {t("editGiftCard.active")} <ActiveDot active={true} />
        </div>

        {isLoading &&
          createPortal(<Spinner />, document.getElementById("portal"))}

        <div className="gift-card__card-details">
          <div className="gift-card__card-preview">
            <GiftcardGallery setSelectedImage={setSelectedImage} selectedImage={selectedImage} />
          </div>

          <div className="gift-card__card-data">
            <label className="gift-card__label">
              {t("commonTexts.title")} <span className="mandatory">*</span>
            </label>
            <input
              type="text"
              className="gift-card__input"
              name="title"
              value={cardData.title}
              onInput={(e) => { setCardData((prev) => ({ ...prev, [e.target.name]: sanitizeInput(e.target.value, "giftcardTitle") })) }}
            />
            <label className="gift-card__label">
              {t("commonTexts.description")} <span className="mandatory">*</span>
            </label>
            <textarea
              type="text"
              className="gift-card__input"
              name="body_html"
              rows={3}
              value={cardData.body_html}
              onInput={(e) => { setCardData((prev) => ({ ...prev, [e.target.name]: sanitizeInput(e.target.value, "textarea") })) }}
            />
            <label className="gift-card__label">
              {t("commonTexts.terms")} <span className="mandatory">*</span>
            </label>
            <textarea
              type="text"
              className="gift-card__input"
              name="terms"
              rows={3}
              value={cardData.terms}
              onInput={(e) => { setCardData((prev) => ({ ...prev, [e.target.name]: sanitizeInput(e.target.value, "textarea") })) }}
            />
            <div className="gift-card__quantity-container">
              <div className="gift-card__label">
                {t("createGiftcard.GcPurchaseQuantity")} <span className="mandatory">*</span>
              </div>
              <div className="gift-card__quantity-inputs">
              <label>
                  {t("createGiftcard.buyForSelfQuantity")}
                  <input
                    type="number"
                    className="gift-card__input"
                    name="self_purchase_quantity"
                    value={cardData.self_purchase_quantity}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
                          e.preventDefault();
                      }
                    }}
                  />
                </label>
                <label>
                  {t("createGiftcard.sendAsGiftQuantity")}
                  <input
                    type="number"
                    className="gift-card__input"
                    name="gift_quantity"
                    value={cardData.gift_quantity} // Default value is 1
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "." || e.key === "e" || e.key === "E"|| e.key === "+" || e.key === "-" ) {
                          e.preventDefault();
                      }
                    }}
                  />
                </label>
              </div>
            </div>
            <div className="gift-card__label">
              {t("editGiftCard.gcValidity")} <span className="mandatory">*</span>
            </div>

            <div>
              {
                renderValidityUi()
              }
            </div>
          </div>
        </div>

        <div className="gift-card__variant-data">
          <div className="gift-card__variant-grid-row">
            <label className="gift-card__label">
              {t("createGiftcard.variantName")} <span className="mandatory">*</span>
            </label>
            <label className="gift-card__label">
              {t("createGiftcard.denomination")} <span className="mandatory">*</span>
            </label>
            <label></label>
          </div>

          {cardData.variants.map((item, index) => (
            <div className="gift-card__variant-grid-row" key={index}>
              <input
                type="text"
                className="gift-card__variant-input-title gift-card__input"
                id={index}
                name="option1"
                value={item.option1}
                onInput={handleVariant}
                onWheel={(e) => e.target.blur()}
              />
              <input
                type="text"
                className="gift-card__variant-input-price gift-card__input"
                id={index}
                name="price"
                value={item.price}
                onInput={handleVariant}
                onWheel={(e) => e.target.blur()}
              />
              <img src={DeleteIcon} alt="" id={index} onClick={handleDelete} />
            </div>
          ))}
        </div>
        <div className="gift-card__append-variant" onClick={handleAppend}>
          {t("editGiftCard.appendVariant")}
          <span>
            <FaPlus />
          </span>
        </div>

        {/* error */}
        {isError && <div className="gift-card__error">{isError}</div>}

        <PrimaryBtn $primary onClick={handleUpdate}>
          {t("editGiftCard.save")}
        </PrimaryBtn>
      </div>
    )
  );
};

export default EditGiftCard;
